/**
 * Onboarding hub UX types — derived from the sandbox prototype, used by the
 * four hub variants under `pages/Onboarding/variants/`. This is the
 * rendering-side data model: flat list of compliance subsections (not the
 * five grouped sections in the deliverables doc), each becoming a discrete
 * card. The UX visually clusters them under category headers (Use Case &
 * Intent / Organization & Entity / Wallets / Agreements).
 *
 * Reconciliation with `data/mockOnboarding.ts`:
 *   `data/mockOnboarding.ts` (existing) is the application-state model that
 *   wires up to the eventual onboarding API — `OnboardingApplication`,
 *   `OnboardingDocument`, `OnboardingAgreement`, six seeded states. Concerns
 *   are different (backend-shaped vs. hub-UX-shaped) so the two models live
 *   side-by-side. They will converge once the onboarding API lands and the
 *   hub renders against real application state instead of mock scenarios.
 */

export type ApplicationState =
    | "NotStarted"
    | "InProgress"
    | "UnderReview"
    /** Reviewed and approved; the agreements are out for e-signature. */
    | "AwaitingAgreements"
    | "InformationRequested"
    | "Approved"
    | "Declined";

/**
 * Two-phase split of the application lifecycle, derived from
 * `ApplicationState`. The phases use disjoint vocabulary on the
 * applicant's hub so that a single SectionStatus value (e.g. `Complete`)
 * never has to mean two different things in the UI:
 *
 *   - `submission` — applicant is filling things out. The vocab is
 *     "Not Started / In Progress / Saved". No reviewer-side states.
 *
 *   - `review` — application has been submitted (or progressed beyond).
 *     The vocab is "Under Review / Action Required / Approved". The
 *     terminal states (Approved, Declined) collapse into this phase
 *     because their section badges still read as review-vocabulary.
 */
export type ApplicationPhase = "submission" | "review";

export function applicationPhase(state: ApplicationState): ApplicationPhase {
    if (state === "NotStarted" || state === "InProgress") return "submission";
    return "review";
}

/** Per-subsection badge state. "Locked" is a treatment of NotStarted
 *  with `prerequisiteLabel` set, not a separate status.
 *
 *  Post-submission, the section's status is a roll-up of slot
 *  `reviewState`s — see `computeSectionStatusFromSlots` in
 *  `OnboardingShared.tsx`.
 */
export type SectionStatus =
    | "NotStarted"
    | "InProgress"
    | "WaitingOnYou"
    | "WaitingOnSolomon"
    | "NeedsAttention"
    | "Complete";

/** Per-slot review state. Set on every slot once the application has
 *  been submitted; undefined pre-submission.
 *
 *  - `PendingReview` — submitted and waiting on the reviewer; neutral
 *    indicator, no edits possible.
 *  - `Approved` — reviewer signed off; success indicator, immutable.
 *  - `Rejected` — reviewer flagged the slot; ember warning, the
 *    reviewer's `reviewerNote` is shown inline, the slot is editable
 *    (the only path to edit a slot post-submission).
 */
export type SlotReviewState = "PendingReview" | "Approved" | "Rejected";

/** Per-slot completion state, derived from the backend step payload by
 *  `slotStatusFromPayload` and overlaid in `scenarioFromServer`.
 *
 *  Distinct from `SlotReviewState`, which is the reviewer's verdict after
 *  submission. This one answers the applicant's question while drafting:
 *  has this particular sub-row been submitted yet?
 *
 *  - `NotStarted` — required, nothing on file.
 *  - `Complete`   — every payload field that fulfils the slot is set.
 *  - `Optional`   — not required, nothing on file. Rendered in the muted
 *    tone so a fully-saved section never reads as if a row were missing.
 */
export type SlotStatus = "NotStarted" | "Complete" | "Optional";

/**
 * How a slot is fulfilled in its step's payload — the keys that must all
 * carry a non-empty string before the slot counts as submitted.
 *
 * Authored on the slot in `spec/onboardingSpec.ts` so the sub-row and the
 * payload keys behind it stay together, and mirrors the backend's own
 * completeness rules (`OnboardingStepService.legalEntityPayloadIsComplete`
 * and the per-step `statusAfterApplicantSave` branch). A slot without this
 * has no derivable state and renders no badge.
 */
export interface SlotFulfilment {
    fields: readonly string[];
    /** The step completes without this slot — an unfilled row reads
     *  "Optional" rather than "Not Started". */
    optional?: boolean;
}

/**
 * One submission slot — one thing the user submits inside a subsection.
 *
 * Slot numbering at render time is `${subsectionOrdinal}.${slotIdx + 1}`
 * (e.g. slot 2.2 means the second slot inside subsection 2). The number
 * is computed by the renderer, not stored here.
 *
 * `acceptable` carries the alternatives that satisfy the slot when the user
 * gets a choice — e.g. a "Current standing certificate" slot accepts a
 * Certificate of Good Standing OR Existence OR Status. Stored as data only;
 * not rendered on the hub itself (surfaced inside the per-slot upload UI
 * downstream).
 *
 * `conditional` flags slots that only apply under specific triggers
 * (jurisdiction, entity type, signing path, etc.). The hub renders the
 * description hint underneath the title in muted text.
 */
export interface Slot {
    /** Descriptive name of what the user submits in this slot. */
    title: string;
    /** What the user is uploading and why — 1–2 sentences. Renders in
     *  the right panel's "Description" block when this slot is focused. */
    description?: string;
    /** When the slot only applies under specific triggers, describes when. */
    conditional?: {
        /** Short slug — e.g. "non-Hague-Convention jurisdiction". */
        trigger: string;
        /** User-facing — e.g. "Required only when entity docs come from...". */
        description: string;
    };
    /** Alternatives that satisfy this slot. The user picks ONE at upload time. */
    acceptable?: string[];
    /**
     * When true, the right-panel header reads "Examples of Accepted
     * Documents" instead of pluralizing "Accepted Document(s)" by count.
     * Use for slots where the listed items are *illustrative variants*
     * of the same artifact (the user typically supplies one form
     * matching their jurisdiction or context, not freely picking from
     * the list).
     */
    examplesOnly?: boolean;
    // (Reserved for future per-slot rendering modes. The prior
    //  "person-kyc" kind was removed in the BD-aligned trim — UBO
    //  collection is now name + ownership % only.)
    /**
     * Third-party verification flow that fulfills this slot — the user is
     * routed out to the named provider rather than uploading a file. When
     * set, the slot detail renders a "Start Verification" CTA in place of
     * the dropzone + upload-button pair.
     *
     * Examples: Persona for biometric liveness (must be on-device; can't be
     * uploaded); DocuSign for MSA signing; Sumsub / Onfido inquiries; etc.
     */
    verification?: {
        /** Provider name — surfaced in the panel ("via Persona"). */
        provider: string;
        /** CTA label — e.g. "Start Verification", "Sign with DocuSign". */
        ctaLabel: string;
    };

    /** Payload keys that fulfil this slot — see `SlotFulfilment`.
     *  Authored in the spec; drives `status` below. */
    fulfilment?: SlotFulfilment;

    /** Live completion state, set by the `scenarioFromServer` overlay
     *  from the step payload. Never authored in the spec: a slot only
     *  has one once real application data is in hand. */
    status?: SlotStatus;

    /** Per-slot review state, set once the application has been
     *  submitted. Undefined pre-submission. Drives the per-slot
     *  visual treatment + which slots are editable to the applicant. */
    reviewState?: SlotReviewState;

    /** Reviewer's reason text, attached when `reviewState === "Rejected"`.
     *  Surfaces inline in the slot detail panel above the editable
     *  input. Plain text only at v1. */
    reviewerNote?: string;

    /** Timestamps for a rejected slot. Drives the "Solomon Labs Note"
     *  card's due-by + rejected-at footer (mirrors `issuerRequested`
     *  for adhoc requests). Only meaningful when
     *  `reviewState === "Rejected"`. */
    rejection?: {
        /** ISO timestamp of when the slot was rejected. */
        rejectedAt: string;
        /** Optional ISO timestamp deadline for the applicant to
         *  resubmit. */
        deadline?: string;
    };

    /** Set on slots created by the issuer as adhoc requests during
     *  review (not part of the original spec). Drives the "Issuer
     *  requested" tag in the UI and the section-level
     *  "needs attention" roll-up. */
    issuerRequested?: {
        /** ISO timestamp of when the request was raised. */
        requestedAt: string;
        /** Optional ISO timestamp deadline for the applicant. */
        deadline?: string;
        /** Severity tag the reviewer set when raising the request. */
        severity?: "minor" | "substantive";
    };

    /** Preserved prior submissions when a slot has been resubmitted
     *  post-rejection. Most-recent on top. Renders as a collapsible
     *  "Previous version" affordance below the active input. The
     *  payload shape varies by slot type — for documents an object
     *  key + filename; for narratives the text; for structured
     *  forms the field map. */
    versionHistory?: Array<{
        submittedAt: string;
        rejectedAt?: string;
        rejectedReason?: string;
        payload: unknown;
    }>;
}

/** Stable identifiers for every subsection in the deliverables doc. */
export type SubsectionId =
    | "use-case"
    | "legal-entity"
    | "tax-identification"
    | "signatory-authority"
    | "beneficial-ownership"
    | "sanctions-screening"
    | "source-of-funds"
    | "wallet-primary"
    | "review-submit"
    | "msa"
    | "order-form";

/** Visual buckets the cards group under. */
export type Category = "use-case" | "organization" | "wallets" | "signatory" | "review" | "agreements";

/** Card visual variant.
 *   standard      — user-actionable subsection (most cards).
 *   solomon-run   — Solomon executes (sanctions §B.6, signature meta §D.4).
 *                   No "your turn" CTA; muted; status maps to a 3-state set.
 *   recommended   — non-blocking but counted (source-of-funds §B.7).
 *   deferred      — Path B subsections (§B.8, §C.5). Visible only when the
 *                   Use Case selection triggers them; rendered with a
 *                   "Deferred Path" ribbon and disabled chrome.
 */
export type CardTreatment = "standard" | "solomon-run" | "recommended" | "deferred";

export interface OnboardingSubsection {
    id: SubsectionId;
    category: Category;
    /**
     * Internal slug-based number. Retained on the data model for stable
     * ordering / routing; the rendered top-level label uses sequential
     * numbering (1., 2., 3., ...) computed at render time. Children render
     * no number — just the title.
     */
    number: string;
    /** Short title (no number prefix). */
    title: string;
    /** One-line description; shown under the title. */
    description: string;
    /**
     * The compliance question this subsection exists to answer. Authoritative
     * over the title: a title names one way to answer the question, and
     * implementations change while the question does not. Every slot has to
     * earn its place against this — a slot that answers nothing here is dead
     * weight. Optional while the older sections are still titled after their
     * artifact rather than their question.
     */
    complianceQuestion?: string;
    /**
     * Submission slots — one entry per thing the user submits. Renders as a
     * numbered list (`${subsectionOrdinal}.${slotIdx + 1}`) inside the right
     * detail panel.
     */
    slots: Slot[];
    treatment: CardTreatment;
    status: SectionStatus;
    /**
     * Single-parent nesting. When set, this subsection renders inside the
     * parent's card frame as a child instead of as a top-level card. Cards
     * that have multiple upstream dependencies (today: §D.1) stay
     * top-level and use `preconditions` instead.
     */
    parentId?: SubsectionId;
    /**
     * Multi-dependency lock — the upstream subsections that must all be
     * Complete before this card becomes actionable. Rendered as a clickable
     * Preconditions chip-list inside the card. Today this is only §D.1.
     */
    preconditionIds?: SubsectionId[];
    /** Locked treatment — visible but blocked by an upstream prerequisite. */
    prerequisiteLabel?: string;
    /** ETA (rendered for actionable user states). */
    estimateLabel?: string;
    /** SLA (rendered for WaitingOnSolomon). */
    slaLabel?: string;
    /** "You'll need..." hint for NotStarted (and not locked). */
    readinessHint?: string;
    /** NeedsAttention reason copy, or WaitingOnYou follow-up note. */
    attentionReason?: string;
    /** Quiet completion summary line for Complete subsections. */
    completionSummary?: string;
    /** Last review activity timestamp; shown alongside SLA. */
    lastUpdatedLabel?: string;
    /**
     * Proof-point counts for this subsection — total distinct proof points
     * for this scenario's applicant context (varies with US vs non-US,
     * # of UBOs, # of wallets, multisig vs EOA, LP path, etc.).
     * Three sub-counts always sum to ≤ totalProofPoints; the remainder is
     * "not yet started". Zero defaults if omitted on construction.
     */
    totalProofPoints: number;
    satisfiedProofPoints: number;
    outstandingProofPoints: number;
    pendingReviewProofPoints: number;
    /** "×2 UBOs · 12 proofs total" — explicit per-instance multiplier copy. */
    multiplierLabel?: string;
    /** 2–3 short proof-point names to give the count texture. */
    proofExamples?: string[];
    /**
     * False hides the card (deferred subsection not triggered, or
     * conditional subsection not applicable). Cards still in the data so a
     * future event flag can flip them on without changing the render order.
     */
    isApplicable: boolean;
    /**
     * Slot semantics. Most subsections are step-lists — slots are an ordered
     * sequence of deliverables that render numbered (2.1, 2.2, …) on the
     * left list. A few subsections are item-lists — slots are repeated
     * instances of the same thing (one entry per wallet, per UBO, per
     * signer). Item slots render with a status dot instead of a number,
     * and the slot title becomes a label for the instance (e.g. "Wallet 1"
     * → wallet address once submitted). Default = step-list.
     */
    slotsAsItems?: boolean;
    /**
     * Subsection-level verification flow. When set, the subsection has no
     * internal slots — its single action is to route the user to the named
     * provider (e.g. DocuSign for the MSA). The detail panel skips the
     * "Begin →" CTA and renders the provider-specific CTA instead.
     *
     * Use this for one-shot subsections where listing a single slot would
     * just restate the section title. Slot-level `verification` still
     * applies for cases where a subsection has multiple slots and only
     * some of them route to a provider (e.g. UBO biometric liveness inside
     * Beneficial Ownership).
     */
    verification?: {
        provider: string;
        ctaLabel: string;
    };
}

/** Resolved precondition reference — produced at render time by joining a
 *  parent subsection's `preconditionIds` against the live subsection list. */
export interface PreconditionRef {
    refId: SubsectionId;
    number: string;
    title: string;
    status: SectionStatus;
}

/**
 * A row on the left rail — a subsection row, or one of its slot sub-rows
 * when `slotIdx` is set. Produced by `findNextIncompleteTarget` and
 * threaded into every Save form as the "Continue to ..." destination.
 */
export interface NextStepTarget {
    id: SubsectionId;
    title: string;
    /** Set when the target is a sub-row rather than the section row. */
    slotIdx?: number;
}

/** Parent/child tree built at render time from the flat subsection list. */
export interface OnboardingNode {
    subsection: OnboardingSubsection;
    children: OnboardingSubsection[];
    preconditions?: PreconditionRef[];
}

/**
 * Build the parent/child tree from a flat subsection list. Top-level nodes
 * are subsections that are applicable and have no `parentId`. Children are
 * applicable subsections with a `parentId` that resolves to a top-level
 * applicable parent (otherwise they're promoted to top-level so the data
 * never disappears).
 */
export function buildOnboardingTree(subsections: OnboardingSubsection[]): OnboardingNode[] {
    const byId = new Map<SubsectionId, OnboardingSubsection>();
    for (const s of subsections) byId.set(s.id, s);

    const childrenByParent = new Map<SubsectionId, OnboardingSubsection[]>();
    const topLevel: OnboardingSubsection[] = [];

    for (const sec of subsections) {
        if (!sec.isApplicable) continue;
        const parent = sec.parentId ? byId.get(sec.parentId) : undefined;
        if (parent?.isApplicable) {
            const list = childrenByParent.get(sec.parentId!) ?? [];
            list.push(sec);
            childrenByParent.set(sec.parentId!, list);
        } else {
            topLevel.push(sec);
        }
    }

    return topLevel.map((sec) => {
        const node: OnboardingNode = {
            subsection: sec,
            children: childrenByParent.get(sec.id) ?? []
        };
        if (sec.preconditionIds && sec.preconditionIds.length > 0) {
            node.preconditions = sec.preconditionIds
                .map((pid) => byId.get(pid))
                .filter((s): s is OnboardingSubsection => !!s)
                .map((s) => ({
                    refId: s.id,
                    number: s.number,
                    title: s.title,
                    status: s.status
                }));
        }
        return node;
    });
}

export type BannerKind =
    | "your_turn"
    | "we_are_reviewing"
    /** Approved; the agreements are with the signatory. */
    | "agreements_out"
    | "action_required"
    | "approved";

export interface BannerSpec {
    kind: BannerKind;
    headline: string;
    ctaLabel?: string;
    ctaTargetId?: SubsectionId;
}

export interface LifecycleDates {
    started: string;
    submitted?: string;
    active?: string;
}

export interface OnboardingScenario {
    name: string;
    state: ApplicationState;
    orgName: string;
    lifecycle: LifecycleDates;
    banner: BannerSpec;
    /** Flat list of every subsection that exists for this applicant. Cards
     *  with `isApplicable: false` are hidden but kept in the data so the
     *  ordering is stable when a use-case toggle flips one on. */
    subsections: OnboardingSubsection[];
    /** Approved-only — whether to play the celebration on mount. */
    celebrate?: boolean;
    /** Declined-only — applicant-facing reason copy. */
    declineReason?: string;
    /** SLA string used in welcome strip + banner copy. */
    sla: string;
    /**
     * Conditional-slot triggers that are flipped on for this scenario.
     * A slot with `conditional.trigger === "X"` becomes visible when
     * `triggers["X"] === true`. Default empty map = all conditional
     * slots hidden. Future: inferred from user-submitted data.
     */
    triggers?: Record<string, boolean>;
}

/**
 * Rail items that exist but cannot gate submission.
 *
 * The two agreements are sent by the issuer's approval, so requiring them
 * before submission would be the cycle the review gate removes — this
 * mirrors `AGREEMENT_PREREQUISITE_STEPS` on the API, which is what
 * submit-for-review actually checks. Review and submit hosts the Submit
 * button itself, so it can never be complete before the thing it triggers.
 */
const NON_BLOCKING_SUBSECTION_IDS: ReadonlySet<SubsectionId> = new Set(["review-submit", "msa", "order-form"]);

/** Subsections that count toward submission gating — i.e. blocking, applicant-
 *  actionable subsections. Solomon-run, recommended, non-applicable, and the
 *  subsections listed above do not block submission. */
export function blockingSubsections(subsections: OnboardingSubsection[]): OnboardingSubsection[] {
    return subsections.filter(
        (s) =>
            s.isApplicable &&
            !NON_BLOCKING_SUBSECTION_IDS.has(s.id) &&
            s.treatment !== "solomon-run" &&
            s.treatment !== "recommended" &&
            s.treatment !== "deferred"
    );
}

export function blockingRemaining(subsections: OnboardingSubsection[]): OnboardingSubsection[] {
    return blockingSubsections(subsections).filter((s) => s.status !== "Complete");
}

export function isSubmittable(subsections: OnboardingSubsection[]): boolean {
    return blockingRemaining(subsections).length === 0;
}

/**
 * Render order of categories.
 *
 * `signatory` is its own bucket rather than part of `organization` because
 * of where it has to land: the applicant names who signs immediately before
 * reading the application back and submitting it, so the last thing they
 * enter is the person who will execute the agreements. Inside
 * `organization` it would sort up with the entity paperwork.
 *
 * This is the applicant's order only. The issuer console reviews the same
 * application with the signatory back at §B.4, next to the authorization
 * documents, and tracks the backend's `ONBOARDING_STEP_ORDER` rather than
 * this list. The divergence is intended; neither side should be "fixed" to
 * match the other.
 */
export const CATEGORY_ORDER: Category[] = ["use-case", "organization", "wallets", "signatory", "review", "agreements"];

/**
 * Bucket items into `CATEGORY_ORDER`, keeping declaration order inside each
 * bucket. The rail, the hub stagger and the slot numbering all read the
 * list in this one order, so they share this helper rather than each
 * re-deriving it — a section's rendered position is decided here or nowhere.
 */
export function groupByCategory<T>(items: readonly T[], categoryOf: (item: T) => Category): Map<Category, T[]> {
    const grouped = new Map<Category, T[]>();
    for (const cat of CATEGORY_ORDER) grouped.set(cat, []);
    for (const item of items) grouped.get(categoryOf(item))?.push(item);
    return grouped;
}

/** `groupByCategory` flattened back to a list — render order, top to bottom. */
export function orderByCategory<T>(items: readonly T[], categoryOf: (item: T) => Category): T[] {
    const grouped = groupByCategory(items, categoryOf);
    return CATEGORY_ORDER.flatMap((cat) => grouped.get(cat) ?? []);
}
